import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useQuery } from 'react-query';
import { useSessionStorage } from 'usehooks-ts';

import { useThemeConfig } from '@/lib/hooks/useThemeConfig';
import { sanitizeApiKey, validateApiKey } from '@/lib/utils/apiKey';
import { getClientKey } from '@/lib/utils/getClientKey';
import { apiGetAppConfig } from '@/services/auth.api';
import { APP_CONFIG_CONSTANTS } from '../../constants/appConfig.constants';
import { useApiInterceptors } from '../../lib/hooks/useApiInterceptors';
import { useThemeManager } from '../../lib/hooks/useThemeManager';
import { useTokenManager } from '../../lib/hooks/useTokenManager';
import type { AppConfigContextProps } from '../../types/appConfigTypes';
import ErrorBanner from '../template/ErrorBanner';
import ErrorBoundary from '../template/ErrorBoundary';
import PageLoader from '../template/PageLoader';
import { useExternalCurrencyStore } from '@/store';

const AppConfigContext = createContext<AppConfigContextProps | undefined>(
  undefined
);

const isProd = import.meta.env.VITE_ENV_INSTANCE === 'PROD';

const AppConfigProvider = ({ children }: { children: ReactNode }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [colorScheme, setColorScheme] = useState<'auto' | 'light' | 'dark'>(
    APP_CONFIG_CONSTANTS.DEFAULT_THEME
  );
  const { setExternalCurrencyEnabled } = useExternalCurrencyStore();
  const [isTokenReady, setIsTokenReady] = useState(false); // NEW

  const [apiKey, setApiKey] = useSessionStorage(
    APP_CONFIG_CONSTANTS.API_KEY_STORAGE_KEY,
    ''
  );
  const { setAppConfig } = useThemeConfig();

  // Get effective API key (production uses stored key, development uses client key)
  const effectiveApiKey = useMemo(() => {
    return isProd ? apiKey : getClientKey();
  }, [apiKey]);

  const isApiKeyValid = useMemo(
    () => validateApiKey(effectiveApiKey),
    [effectiveApiKey]
  );

  // Load app configuration (this must happen first)
  const {
    isLoading: isAppConfigLoading,
    refetch: refetchAppConfig,
    error: appConfigError,
  } = useQuery({
    queryKey: ['appConfig'],
    queryFn: apiGetAppConfig,
    enabled: !isInitialized,
    onSuccess: (res) => {
      try {
        const data = res?.data?.data;
        if (data) {
          setAppConfig(data);
          setColorScheme(
            data.ui?.theming?.themeMode || APP_CONFIG_CONSTANTS.DEFAULT_THEME
          );

          // Handle API key from response
          const rawKey = data.apiKey;
          if (rawKey) {
            const cleanedKey = sanitizeApiKey(rawKey);
            if (validateApiKey(cleanedKey)) {
              setApiKey(cleanedKey);
              setError(null);
            } else {
              setError('Invalid API key format received');
            }
          }
        }
        setIsInitialized(true);
        const externalCurrencyEnabled =
          res?.data?.data?.app?.externalCurrency?.enable ?? false;
        setExternalCurrencyEnabled(externalCurrencyEnabled);

        console.log('External Currency Enabled:', externalCurrencyEnabled);

      } catch (err) {
        console.error('Error processing app config:', err);
        setError('Failed to process configuration');
        setIsInitialized(true);
      }
    },
    onError: (err) => {
      console.error('Failed to load app config:', err);
      setError('Failed to load configuration');
      setIsInitialized(true);
    },
    refetchOnWindowFocus: false,
    retry: APP_CONFIG_CONSTANTS.RETRY_ATTEMPTS,
  });

  // Initialize theme management
  useThemeManager();

  // Initialize token management (only after app config is loaded and API key is available)
  const {
    refreshToken,
    getTrialToken,
    token,
    isAuthenticated,
    isFetchingToken,
  } = useTokenManager(effectiveApiKey);

  // Handle API key errors
  const handleApiKeyError = () => {
    setError('Invalid API key');
  };

  // Set up API interceptors
  useApiInterceptors(
    effectiveApiKey,
    token,
    refreshToken,
    isAuthenticated,
    handleApiKeyError
  );

  // Initialize tokens after app config is loaded
  useEffect(() => {
    if (!isInitialized || !effectiveApiKey || !isApiKeyValid) return;

    const initializeTokens = async () => {
      try {
        if (isAuthenticated) {
          await refreshToken();
        } else {
          await getTrialToken();
        }
        setIsTokenReady(true); // Only set ready after refresh/trial
      } catch (err) {
        console.error('Token initialization failed:', err);
        setError('Failed to initialize authentication');
        setIsTokenReady(false);
      }
    };

    setIsTokenReady(false); // Block children until ready
    initializeTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, effectiveApiKey, isApiKeyValid, isAuthenticated]);

  const retryConfig = useCallback(async () => {
    setError(null);
    await refetchAppConfig();
  }, [refetchAppConfig]);

  const contextValue = useMemo(
    () => ({
      isLoading: isAppConfigLoading,
      colorScheme,
      isApiKeyValid,
      clientApiKey: effectiveApiKey,
      error: error || (appConfigError ? 'Configuration load failed' : null),
      retryConfig,
      refreshToken,
      getTrialToken,
      isFetchingToken,
    }),
    [
      isAppConfigLoading,
      colorScheme,
      isApiKeyValid,
      effectiveApiKey,
      error,
      appConfigError,
      retryConfig,
      refreshToken,
      getTrialToken,
      isFetchingToken,
    ]
  );

  return (
    <AppConfigContext.Provider value={contextValue}>
      {error && <ErrorBanner message={error} onRetry={retryConfig} />}
      {isAppConfigLoading || !isTokenReady ? (
        <PageLoader />
      ) : (
        <ErrorBoundary>{children}</ErrorBoundary>
      )}
    </AppConfigContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within a AppConfigProvider');
  }
  return context;
};

export default AppConfigProvider;
